import clsx from 'clsx';
import { useValueAddNarrative } from '@/hooks/use-value-add-narrative';
import type { SignalFieldResolverContext } from '@/hooks/use-signal-field-resolver';
import { Skeleton } from '@/components/ui/Skeleton';
import { PhaseCard, type PhaseMetric } from './PhaseCard';

// deal-signal-overhaul Phase 4 Session 3 (2026-05-12) + Lovable-parity rewrite
// (2026-05-12).
//
// Zone 4 of the Signal page — 3-column "Value-Add Story" phase strip mirroring
// Lovable's `ValueAddStory` (`dashboard-arcanum/src/pages/PublicDealView.tsx:
// 847-860`). Cards arranged in a 3-col grid with `gap-9` (36px) so the inter-
// card `→` arrow has room to render outside each card's box. Bullets from the
// sidecar narrative are parsed into label/value metric pairs at the card
// footer (split on first `:`).

const STEP_NUMBER = ['01', '02', '03'] as const;

function parseBulletAsMetric(bullet: string): PhaseMetric {
  const colonIdx = bullet.indexOf(':');
  if (colonIdx === -1) {
    return { label: bullet, value: '' };
  }
  return {
    label: bullet.slice(0, colonIdx).trim(),
    value: bullet.slice(colonIdx + 1).trim(),
  };
}

export interface ValueAddStoryProps {
  /** Auth (conversation) OR public (deal share) resolver context. */
  context: SignalFieldResolverContext;
  className?: string;
}

export function ValueAddStory({ context, className }: ValueAddStoryProps) {
  const { phases, isReady, narrativeMissing } = useValueAddNarrative(context);

  if (!isReady) {
    return (
      <div
        data-testid="value-add-story"
        data-state="loading"
        className={clsx('grid grid-cols-1 md:grid-cols-3 gap-9', className)}
      >
        {STEP_NUMBER.map((step) => (
          <div
            key={step}
            data-testid="value-add-story-skeleton-card"
            className="flex flex-col gap-1.5 px-2.5 py-2 border border-panel-border border-l-2 border-l-accent bg-panel-bg"
          >
            <Skeleton variant="line" className="w-1/3" />
            <Skeleton variant="line" className="w-full" />
            <Skeleton variant="line" className="w-5/6" />
            <Skeleton variant="line" className="w-2/3 mt-2" />
          </div>
        ))}
      </div>
    );
  }

  if (narrativeMissing) {
    return (
      <div
        data-testid="value-add-story"
        data-state="empty"
        className={clsx(className)}
      >
        <div
          data-testid="value-add-story-empty-card"
          className="flex flex-col gap-2 px-4 py-6 border border-panel-border bg-panel-bg text-center"
        >
          <span className="text-[9px] font-mono text-accent uppercase tracking-[1.3px] font-bold">
            Value-Add Narrative
          </span>
          <p className="text-xs font-mono text-text-muted">
            Value-add narrative not configured for this model.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="value-add-story"
      data-state="ready"
      className={clsx('flex flex-col', className)}
    >
      <div className="flex items-center gap-2.5 mb-2">
        <span
          aria-hidden="true"
          className="w-1 h-1 rounded-full bg-accent shrink-0"
          style={{ boxShadow: '0 0 4px rgba(0,200,83,0.6)' }}
        />
        <h3 className="text-[10px] font-mono uppercase tracking-[2px] text-accent font-bold">
          Value-Add Story
        </h3>
        <span className="flex-1 h-px bg-panel-border" />
      </div>
      <div className="relative grid grid-cols-1 md:grid-cols-3 gap-9">
      {phases.map((phase, idx) => {
        const metrics = phase.bullets.map(parseBulletAsMetric).filter((m) => m.value !== '');
        const stepNumber = STEP_NUMBER[idx] ?? String(idx + 1).padStart(2, '0');
        const showArrow = idx < phases.length - 1;
        return (
          <div key={`${phase.title}-${idx}`} className="relative min-w-0">
            <PhaseCard
              stepNumber={stepNumber}
              title={phase.title}
              body={phase.narrative}
              metrics={metrics}
              status={phase.status}
            />
            {showArrow && (
              <span
                aria-hidden="true"
                className="hidden md:block absolute -right-[21px] top-3.5 z-10 text-[11px] font-mono font-bold text-accent/35"
              >
                →
              </span>
            )}
          </div>
        );
      })}
      </div>
    </div>
  );
}
